import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsService } from '../services/notificationsService';

const PUSH_BROADCAST_CHANNEL = 'agendario-push';
const PUSH_ENDPOINT_KEY = 'agendario_push_endpoint'; // endpoint registrado com sucesso no backend
const PUSH_ROTATED_KEY = 'agendario_push_rotated_at'; // timestamp (ms) do último subscribe fresco
const ROTATE_AFTER_MS = 24 * 60 * 60 * 1000; // rotaciona a subscription a cada 24h

// Retry para chamadas de rede frágeis (a API de notificações pode dar timeout intermitente):
// sem isto, o (re)registro falhava CALADO e a subscription ficava dessincronizada = sem push.
async function retry(fn, attempts = 3, delayMs = 1500) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  throw lastErr;
}

/**
 * Hook para gerenciar Web Push Notifications.
 *
 * Retorna:
 * - pushSupported: browser suporta Push API?
 * - permissionState: 'default' | 'granted' | 'denied' | null
 * - isSubscribed: este browser tem subscription ativa?
 * - isLoading: operacao em andamento?
 * - requestPermission(): solicitar permissao + subscribe
 * - unsubscribe(): cancelar subscription
 */
export function usePushNotifications() {
  const [pushSupported, setPushSupported] = useState(false);
  const [permissionState, setPermissionState] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initRef = useRef(false);

  // Inicializar: verificar suporte, permissao e subscription existente
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      // Verificar suporte
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      setPushSupported(supported);

      if (!supported) return;

      // Registrar Service Worker (idempotente - se ja registrado, retorna existente)
      try {
        await navigator.serviceWorker.register('/sw.js');
      } catch (err) {
        console.warn('[PushNotifications] Erro ao registrar SW:', err);
        return;
      }

      // Ler estado de permissao atual
      const permission = Notification.permission;
      setPermissionState(permission);

      if (permission === 'granted') {
        // Verificar se este browser ja tem subscription ativa
        await checkExistingSubscription();
      }
    };

    init();
  }, []);

  const checkExistingSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const storedEndpoint = localStorage.getItem(PUSH_ENDPOINT_KEY);
        const rotatedAt = parseInt(localStorage.getItem(PUSH_ROTATED_KEY) || '0', 10);
        const stale = !rotatedAt || (Date.now() - rotatedAt) > ROTATE_AFTER_MS;

        if (!storedEndpoint || storedEndpoint !== subscription.endpoint) {
          // Registro pendente (falha anterior) ou o browser renovou o endpoint → (re)registra.
          console.info('[PushNotifications] Subscription não confirmada / endpoint mudou — registrando...');
          await subscribeToServer();
        } else if (stale) {
          // A subscription pode ter virado "zumbi": o FCM aceita o push (201) mas não entrega,
          // e NÃO devolve 410 — nenhuma API de browser expõe isso. Como não dá pra detectar,
          // rotaciona a cada 24h (unsubscribe + subscribe fresco) para garantir um canal de
          // entrega vivo, sem o usuário precisar fazer NADA.
          console.info('[PushNotifications] Subscription > 24h — rotacionando para garantir canal de push vivo...');
          await rotateSubscription(subscription);
        } else {
          // Endpoint confirmado, inalterado e recente — tudo certo.
          setIsSubscribed(true);
        }
      } else {
        // Permissao granted mas sem subscription - re-subscribe silenciosamente
        await subscribeToServer();
      }
    } catch (err) {
      console.warn('[PushNotifications] Erro ao verificar subscription:', err);
    }
  }, []);

  const subscribeToServer = useCallback(async () => {
    try {
      // Obter VAPID public key do servidor — COM retry (a API pode dar timeout intermitente).
      const vapidPublicKey = await retry(async () => {
        const k = await notificationsService.getVapidPublicKey();
        if (!k) throw new Error('VAPID key indisponível');
        return k;
      });

      const registration = await navigator.serviceWorker.ready;

      // Criar subscription no browser
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Extrair keys
      const p256dh = arrayBufferToBase64(subscription.getKey('p256dh'));
      const auth = arrayBufferToBase64(subscription.getKey('auth'));

      // Registrar no backend — COM retry. Antes falhava CALADO no timeout → subscription
      // dessincronizada = sem push. Agora só desiste após as tentativas.
      const subscriptionId = await retry(async () => {
        const id = await notificationsService.subscribePush(subscription.endpoint, p256dh, auth);
        if (!id) throw new Error('registro sem id');
        return id;
      });

      if (subscriptionId) {
        // Persistir endpoint (detecta renovação silenciosa) + timestamp (base da rotação 24h).
        localStorage.setItem(PUSH_ENDPOINT_KEY, subscription.endpoint);
        localStorage.setItem(PUSH_ROTATED_KEY, Date.now().toString());
        setIsSubscribed(true);
        return true;
      }

      return false;
    } catch (err) {
      console.warn('[PushNotifications] Erro ao subscribe (após retries):', err);
      return false;
    }
  }, []);

  // Rotaciona a subscription: cancela a atual (backend + browser) e cria uma FRESCA.
  // Único jeito de recuperar de uma subscription "zumbi" (canal FCM morto sem 410).
  const rotateSubscription = useCallback(async (oldSubscription) => {
    try {
      if (oldSubscription) {
        try { await notificationsService.unsubscribePushByEndpoint(oldSubscription.endpoint); } catch { /* best-effort */ }
        try { await oldSubscription.unsubscribe(); } catch { /* best-effort */ }
      }
      localStorage.removeItem(PUSH_ENDPOINT_KEY);
      return await subscribeToServer();
    } catch (err) {
      console.warn('[PushNotifications] Erro ao rotacionar subscription:', err);
      return false;
    }
  }, [subscribeToServer]);

  // Ouvir sinalização do SW via BroadcastChannel e via postMessage (fallback).
  // Disparado quando a subscription expira/muda (pushsubscriptionchange no SW).
  // O SW não tem JWT, então delega o re-subscribe autenticado para cá.
  useEffect(() => {
    if (!pushSupported) return;

    const handleResubscribe = () => {
      console.info('[PushNotifications] SW sinalizou pushsubscriptionchange — re-inscrevendo...');
      subscribeToServer();
    };

    // Canal primário: BroadcastChannel
    let channel = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(PUSH_BROADCAST_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
          handleResubscribe();
        }
      };
    }

    // Fallback redundante: ServiceWorker postMessage direto
    const onSwMessage = (event) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        handleResubscribe();
      }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);

    return () => {
      if (channel) channel.close();
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, [pushSupported, subscribeToServer]);

  /**
   * Solicitar permissao ao usuario e subscribe se granted.
   * DEVE ser chamado a partir de uma acao do usuario (click).
   */
  const requestPermission = useCallback(async () => {
    if (!pushSupported) return false;

    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      if (permission === 'granted') {
        const success = await subscribeToServer();
        return success;
      }

      return false;
    } catch (err) {
      console.warn('[PushNotifications] Erro ao requestPermission:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [pushSupported, subscribeToServer]);

  /**
   * Cancelar subscription no browser e no backend.
   */
  const unsubscribe = useCallback(async () => {
    if (!pushSupported) return false;

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Notificar backend antes de cancelar no browser
        await notificationsService.unsubscribePushByEndpoint(subscription.endpoint);
        // Cancelar no browser
        await subscription.unsubscribe();
      }

      localStorage.removeItem(PUSH_ENDPOINT_KEY);
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.warn('[PushNotifications] Erro ao unsubscribe:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [pushSupported]);

  return {
    pushSupported,
    permissionState,
    isSubscribed,
    isLoading,
    requestPermission,
    unsubscribe
  };
}

// --- Helpers ---

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
